/**
 * components/EntitlementGate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Declarative wrapper for any audit-trail page or panel. Renders children when
 * the caller is entitled to `moduleKey`; otherwise renders a friendly empty
 * state that matches the Paper/card style used elsewhere in the dashboard.
 *
 * Typical usage inside a view:
 *
 *   import EntitlementGate from "../components/EntitlementGate.js";
 *   import { FEATURE_MODULES } from "../constants/featureModules.js";
 *
 *   export default function ReportsView() {
 *     return (
 *       <EntitlementGate moduleKey={FEATURE_MODULES.REPORTS}>
 *         <ActualReportsContent />
 *       </EntitlementGate>
 *     );
 *   }
 *
 * Every gated route should wrap its top-level content this way so that a
 * directly-navigated URL still renders the "contact support" state instead
 * of producing a blank page or raw 403 JSON from the backend.
 *
 * @see context/EntitlementContext.js
 */

import React from "react";
import { Card, Container, Row, Col, Button } from "react-bootstrap";
import { useEntitlement } from "../context/EntitlementContext.js";
import { MODULE_LABELS, MODULE_DESCRIPTIONS } from "../constants/featureModules.js";

const REASON_MESSAGES = {
  NO_ENTITLEMENT_ROW:
    "This feature is not enabled for your account. Please contact your vendor administrator or Lusuku support.",
  STATUS_SUSPENDED:
    "This feature is temporarily suspended on your account. Contact Lusuku support to restore access.",
  STATUS_REVOKED:
    "This feature has been withdrawn on your account. Contact Lusuku support for more information.",
  STATUS_EXPIRED:
    "Your entitlement for this feature has expired. Please renew to continue.",
  NO_VENDOR_CONTEXT:
    "Your account is not linked to a vendor tenancy, so this feature cannot be loaded.",
  LOADING: "Loading access permissions…",
  DEFAULT: "Access to this feature is not permitted for your account.",
};

const EntitlementGate = ({ moduleKey, children, fallback = null }) => {
  const { getEntitlement, loading, isEntitled } = useEntitlement();
  const verdict = getEntitlement(moduleKey);

  if (isEntitled(moduleKey)) {
    return <>{children}</>;
  }

  // Allow callers to render their own denied state if desired.
  if (fallback) return fallback;

  const moduleLabel = MODULE_LABELS[moduleKey] || moduleKey;
  const moduleDescription = MODULE_DESCRIPTIONS[moduleKey] || "";
  const isLoading = loading || verdict.reason === "LOADING";

  return (
    <Container fluid style={{ marginTop: 24 }}>
      <Row>
        <Col md={{ span: 8, offset: 2 }}>
          <Card style={{ border: "1px solid #eaeaea", borderRadius: 8 }}>
            <Card.Body style={{ padding: 32, textAlign: "center" }}>
              <div
                style={{
                  fontSize: 36,
                  color: "#b0b0b0",
                  marginBottom: 16,
                }}
              >
                <i className="nc-icon nc-lock-circle-open" aria-hidden="true" />
              </div>
              <Card.Title
                as="h4"
                style={{ marginBottom: 8, color: "#333" }}
              >
                {isLoading ? "Loading…" : `${moduleLabel} is not available`}
              </Card.Title>
              {moduleDescription && !isLoading && (
                <p style={{ color: "#666", marginBottom: 16 }}>
                  {moduleDescription}
                </p>
              )}
              <p
                style={{
                  color: isLoading ? "#888" : "#b25e5e",
                  marginBottom: 24,
                  fontSize: 14,
                }}
              >
                {REASON_MESSAGES[verdict.reason] || REASON_MESSAGES.DEFAULT}
              </p>
              {!isLoading && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  href="mailto:support@lusuku.shop?subject=Feature access request"
                >
                  Contact Lusuku Support
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EntitlementGate;
